import React from 'react';
import { Link } from 'react-router-dom';
import { Code, Github, Linkedin, Mail } from 'lucide-react';

const socialLinks = [
  { href: '#', icon: <Github size={18} />, label: 'GitHub' },
  { href: '#', icon: <Linkedin size={18} />, label: 'LinkedIn' },
  { href: 'mailto:info@fretdev.com', icon: <Mail size={18} />, label: 'Email' },
];

const SocialIcon: React.FC<{ href: string; icon: React.ReactNode; label: string }> = ({ href, icon, label }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '38px',
      height: '38px',
      borderRadius: '10px',
      background: 'rgba(255,255,255,0.06)',
      color: 'rgba(255,255,255,0.5)',
      transition: 'all 0.3s cubic-bezier(.22,1,.36,1)',
      textDecoration: 'none',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.background = 'rgba(245,158,11,0.2)';
      e.currentTarget.style.color = '#fff';
      e.currentTarget.style.transform = 'translateY(-2px)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
      e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
      e.currentTarget.style.transform = 'translateY(0)';
    }}
  >
    {icon}
  </a>
);

const Footer: React.FC = () => {
  const quickLinks = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About' },
    { to: '/services', label: 'Services' },
    { to: '/blog', label: 'Blog' },
    { to: '/certifications', label: 'Certifications' },
    { to: '/contact', label: 'Contact' },
  ];

  const locationLinks = [
    { to: '/locations/dallas', label: 'Dallas' },
    { to: '/locations/carrollton', label: 'Carrollton' },
    { to: '/locations/dfw', label: 'DFW Services' },
  ];

  return (
    <footer
      style={{
        background: 'linear-gradient(to right, #1E3A5F, #0F172A)',
        color: 'rgba(255,255,255,0.6)',
        padding: '64px 0 32px',
      }}
    >
      <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
        <div
          className="footer-grid"
          style={{
            paddingBottom: '48px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          {/* Brand Column */}
          <div>
            <Link
              to="/"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                textDecoration: 'none',
                color: '#fff',
                marginBottom: '16px',
              }}
            >
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div
                  style={{
                    position: 'absolute',
                    inset: '-3px',
                    background: 'linear-gradient(135deg, var(--color-primary-light), var(--color-secondary))',
                    borderRadius: '50%',
                    filter: 'blur(5px)',
                    opacity: 0.5,
                  }}
                />
                <div
                  style={{
                    position: 'relative',
                    background: 'rgba(24,24,27,0.8)',
                    borderRadius: '50%',
                    padding: '5px',
                    display: 'flex',
                  }}
                >
                  <Code size={18} style={{ color: 'var(--color-secondary)' }} />
                </div>
              </div>
              <span
                style={{
                  fontSize: '1.2rem',
                  fontWeight: 800,
                  fontFamily: 'var(--font-heading)',
                }}
              >
                Fret
                <span
                  style={{
                    background: 'linear-gradient(135deg, var(--color-secondary), var(--color-primary-light))',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Dev
                </span>
              </span>
            </Link>

            <p
              style={{
                color: 'rgba(255,255,255,0.45)',
                fontSize: '0.9rem',
                lineHeight: 1.7,
                maxWidth: '340px',
                marginBottom: '24px',
                marginTop: '20px',
              }}
            >
              Enterprise-grade AI and cloud engineering — delivered by a team with
              over a decade of experience at Fortune 500 companies and government agencies.
              Now available to small and medium businesses.
            </p>

            <div style={{ display: 'flex', gap: '10px' }}>
              {socialLinks.map((s) => (
                <SocialIcon key={s.label} href={s.href} icon={s.icon} label={s.label} />
              ))}
            </div>
          </div>

          {/* Quick Links Column */}
          <div>
            <h4
              style={{
                color: '#fff',
                fontSize: '0.95rem',
                fontWeight: 700,
                marginBottom: '20px',
                fontFamily: 'var(--font-heading)',
              }}
            >
              Quick Links
            </h4>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    style={{
                      color: 'rgba(255,255,255,0.5)',
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      transition: 'color 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Locations Column */}
          <div>
            <h4
              style={{
                color: '#fff',
                fontSize: '0.95rem',
                fontWeight: 700,
                marginBottom: '20px',
                fontFamily: 'var(--font-heading)',
              }}
            >
              Service Areas
            </h4>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {locationLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    style={{
                      color: 'rgba(255,255,255,0.5)',
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      transition: 'color 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Get In Touch Column */}
          <div>
            <h4
              style={{
                color: '#fff',
                fontSize: '0.95rem',
                fontWeight: 700,
                marginBottom: '20px',
                fontFamily: 'var(--font-heading)',
              }}
            >
              Get In Touch
            </h4>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <li>
                <a
                  href="mailto:info@fretdev.com"
                  style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', fontSize: '0.9rem', transition: 'color 0.2s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
                >
                  info@fretdev.com
                </a>
              </li>
              <li>
                <Link
                  to="/contact"
                  style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', fontSize: '0.9rem', transition: 'color 0.2s' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
                >
                  Contact Form
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div
          className="footer-bottom"
          style={{
            paddingTop: '24px',
            fontSize: '0.82rem',
            color: 'rgba(255,255,255,0.3)',
          }}
        >
          <p>&copy; {new Date().getFullYear()} FretDev. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
